/**
 * Two legibility sources, both read-only here.
 *
 * `extras.platform_stages[]` is the pipeline's own finding, and the one that
 * raises the verification card. The `issues` keys below are what the edit form
 * recorded while DEV-1820's capture existed, and **no deployed build ever wrote
 * them** -- the capture never left the epic branch and there is no server-side
 * writer, so production carries none. The decoders stay anyway, which is the
 * question to answer before deleting them: documents on shared environments do
 * carry the keys, and `archive/DEV-1820-edit-modal-legibility-capture` would
 * write them again. Whatever writes them next must write the problem case only:
 * a non-empty `issues` rebuilds a conferred document as `warning`.
 */

import { RefusalPreselection } from './reprovation';
import { PlatformStage } from './sick_note';

export const LegibilityStatus = {
  legible: 'legible',
  problem: 'problem',
} as const;

export type LegibilityStatusValue =
  (typeof LegibilityStatus)[keyof typeof LegibilityStatus];

export interface ILegibility {
  status: LegibilityStatusValue | null;
  /** Refusal sub-reason id from the image category. Only set for `problem`. */
  reason: number | null;
  imageProblem: boolean;
}

export const LEGIBILITY_ISSUE_KEYS = {
  status: 'legibility_status',
  reason: 'legibility_reason',
  imageProblem: 'image_legibility_problem',
} as const;

const LEGIBILITY_KEYS: string[] = Object.values(LEGIBILITY_ISSUE_KEYS);

export const isLegibilityIssueKey = (key: string): boolean =>
  LEGIBILITY_KEYS.includes(key);

/** The details view has no other legibility surface, so the alerts panel says it. */
export const LEGIBILITY_ALERT_LABEL =
  'A imagem foi marcada com problema de legibilidade';

/**
 * The pipeline's own legibility finding, which is a different source from the
 * `issues` bag above: that one is what a person answered while the edit form
 * captured it, this one is what the stage decided. Its reader is the
 * verification card.
 *
 * Which outcomes raise the card is `platformLegibilityUnconfirmed` below, and
 * it is stated there only. What belongs here: the answer a person gives is not
 * written anywhere -- the stage row is the pipeline's evidence, not a field to
 * overwrite -- so a raised card lives as long as the document stays
 * unconferred, like the name verification.
 */
const PLATFORM_LEGIBILITY_STAGE_KIND = 'LEGIBILITY';

export const PlatformLegibilityOutcome = {
  legible: 'LEGIBLE',
  illegible: 'ILLEGIBLE',
  borderline: 'BORDERLINE',
} as const;

/**
 * The two findings a person has to answer, worst first: `platformLegibilityFinding`
 * reads the order. `LEGIBLE` settles it.
 */
const RAISES_VERIFICATION: string[] = [
  PlatformLegibilityOutcome.illegible,
  PlatformLegibilityOutcome.borderline,
];

/**
 * True when a legibility stage reported one of the two findings a person
 * answers. Read as an allow-list rather than "anything but `LEGIBLE`": a stage
 * that produced no outcome at all -- `FAILED`, or `SKIPPED` because an earlier
 * fold already rejected the submission -- and an outcome this code does not
 * know say nothing about the image, so the card would state a finding the
 * pipeline never made. Those cases leave the checklist to the rest of its
 * items. Whoever adds an outcome to the vocabulary decides here whether it asks
 * a person.
 *
 * Matched on `kind`, not `name`: the kind names the capability and a process
 * may run more than one stage of it (as it already does for DEDUP), so any
 * legibility stage carrying one of the two findings raises the card -- the
 * worst reading wins. No stage at all says nothing either: the submission
 * predates the capture, the read does not carry the field yet, or the stage
 * ran in shadow mode.
 */
export function platformLegibilityUnconfirmed(
  stages?: PlatformStage[]
): boolean {
  return platformLegibilityFinding(stages) !== null;
}

/**
 * The finding that raised the card, or `null` when none did. With more than one
 * legibility stage the worst reading wins.
 */
export function platformLegibilityFinding(
  stages?: PlatformStage[]
): string | null {
  const outcomes = (stages || [])
    .filter((stage) => stage?.kind === PLATFORM_LEGIBILITY_STAGE_KIND)
    .map((stage) => stage.outcome);

  return (
    RAISES_VERIFICATION.find((finding) => outcomes.includes(finding)) ?? null
  );
}

export function hasLegibilityProblem(issues?: {
  [key: string]: unknown;
}): boolean {
  const { status, imageProblem } = legibilityFromIssues(issues);

  return status === LegibilityStatus.problem || imageProblem;
}

/**
 * The catalogue exposes no code, only a label, so this string is the only handle:
 * a rename stops the refusal dialog opening on that category.
 */
export const LEGIBILITY_REASON_CATEGORY = 'Foto/Arquivo';

export function legibilityFromIssues(issues?: {
  [key: string]: unknown;
}): ILegibility {
  const status = issues?.[LEGIBILITY_ISSUE_KEYS.status];
  const reason = Number(issues?.[LEGIBILITY_ISSUE_KEYS.reason]);
  const known =
    status === LegibilityStatus.legible || status === LegibilityStatus.problem;

  return {
    status: known ? status : null,
    // Taken as stored: an id the catalogue no longer offers matches no option.
    reason: Number.isInteger(reason) && reason > 0 ? reason : null,
    imageProblem: !!issues?.[LEGIBILITY_ISSUE_KEYS.imageProblem],
  };
}

/**
 * Both fields are checked here because legibilityFromIssues decodes them
 * independently.
 */
export function legibilityRefusalPreselection(
  legibility?: ILegibility | null
): RefusalPreselection | null {
  if (legibility?.status === LegibilityStatus.legible) return null;

  const problem = legibility?.status === LegibilityStatus.problem;
  const reason = Number(legibility?.reason);

  if (problem && Number.isInteger(reason) && reason > 0) {
    return {
      reasonIds: [reason],
      expandedCategoryName: LEGIBILITY_REASON_CATEGORY,
    };
  }

  if (problem || legibility?.imageProblem) {
    return { reasonIds: [], expandedCategoryName: LEGIBILITY_REASON_CATEGORY };
  }

  return null;
}
